import type {
  DayPassDto,
  DayPassOfferDto,
  DayPassOfferInput,
  SellDayPassInput,
  UpdateDayPassOfferInput,
} from '@gc/shared';

import { api } from '@/lib/api';

/**
 * Boot Camp day passes (SPEC §6.1) — wire calls and the pure logic behind the
 * screen, kept out of the components so it can be tested in plain node. This
 * workspace has no DOM emulation, so anything worth pinning has to be either a
 * pure function or static markup.
 */

export const DAY_PASSES_PATH = '/day-passes';

/**
 * The screen payload. Mirrors `DayPassScreenDto` on the API, which cannot live
 * in `@gc/shared` while the contracts are frozen for this wave — the four
 * fields are duplicated rather than invented, and the DTOs inside them are the
 * shared ones.
 */
export interface DayPassScreen {
  enabled: boolean;
  /** Today's closing time, ISO — what an until-closing offer is quoted against. */
  closesAt: string;
  offers: DayPassOfferDto[];
  passes: DayPassDto[];
}

/* -------------------------------------------------------------------------- */
/* Wire                                                                       */
/* -------------------------------------------------------------------------- */

export const sellDayPass = (input: SellDayPassInput): Promise<DayPassDto> =>
  api.post<DayPassDto>(DAY_PASSES_PATH, input);

export const createDayPassOffer = (input: DayPassOfferInput): Promise<DayPassOfferDto> =>
  api.post<DayPassOfferDto>(`${DAY_PASSES_PATH}/offers`, input);

export const updateDayPassOffer = (
  id: string,
  input: UpdateDayPassOfferInput,
): Promise<DayPassOfferDto> => api.patch<DayPassOfferDto>(`${DAY_PASSES_PATH}/offers/${id}`, input);

export const deleteDayPassOffer = (id: string): Promise<void> =>
  api.delete<void>(`${DAY_PASSES_PATH}/offers/${id}`);

/* -------------------------------------------------------------------------- */
/* Who the pass is for                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Exactly one of a walk-in name, an open tab, or a registered account — the
 * same shape as a sitting's identity, and for the same reason: holding all
 * three at once is how a chosen tab gets silently dropped on the way out.
 */
export type PassHolder =
  | { kind: 'walkIn'; name: string }
  | { kind: 'tab'; tabId: string; name: string }
  | { kind: 'account'; customerAccountId: string; name: string };

/**
 * The exact wire payload.
 *
 * There is no price here and there is no place to put one: the server reads it
 * off the offer, so a pass cannot be bought for a dinar by editing a request.
 */
export function buildSellPayload(offerId: string, holder: PassHolder): SellDayPassInput {
  switch (holder.kind) {
    case 'tab':
      return { offerId, tabId: holder.tabId };
    case 'account':
      return { offerId, customerAccountId: holder.customerAccountId };
    default:
      return { offerId, customerName: holder.name.trim() };
  }
}

/** Whether the form has enough to submit — mirrors the shared schema's refines. */
export function canSubmitSale(
  offerId: string | null,
  holder: PassHolder,
  enabled: boolean,
): boolean {
  if (!enabled || !offerId) return false;
  switch (holder.kind) {
    case 'tab':
      return holder.tabId !== '';
    case 'account':
      return holder.customerAccountId !== '';
    default:
      return holder.name.trim() !== '';
  }
}

/* -------------------------------------------------------------------------- */
/* Quoting the window                                                         */
/* -------------------------------------------------------------------------- */

/**
 * When a pass sold right now would stop covering play.
 *
 * The same rule the server applies: a fixed window is a promise of N minutes
 * measured from the sale, and a null one is a promise about the day, so it runs
 * to the venue's closing time. Quoted BEFORE the sale, which is why `closesAt`
 * has to come down with the screen — the customer is being told what they are
 * buying while they still have the option not to.
 */
export function previewValidUntil(
  offer: Pick<DayPassOfferDto, 'durationMinutes'>,
  nowMs: number,
  closesAtIso: string,
): Date {
  return offer.durationMinutes === null
    ? new Date(closesAtIso)
    : new Date(nowMs + offer.durationMinutes * 60_000);
}

/** False once `validUntil` has passed. Computed, never stored — same as the API. */
export function isPassLive(pass: Pick<DayPassDto, 'validUntil'>, nowMs: number): boolean {
  return Date.parse(pass.validUntil) > nowMs;
}

/** Offers a cashier can actually sell: the ones the venue has left on sale. */
export function sellableOffers(offers: readonly DayPassOfferDto[]): DayPassOfferDto[] {
  return offers.filter((offer) => offer.isActive);
}

/**
 * Whole IQD from a typed field, or null when the field is not a usable amount.
 * Digits only — IQD has no sub-unit worth typing, and `parseFloat` on money is
 * how rounding bugs start.
 */
export function iqdAmountOf(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}

/** Minutes from a typed field, or null for "until closing". */
export function minutesOf(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= 1 && value <= 24 * 60 ? value : null;
}
